import { IStorage, IStorageLocal, IStorageSession } from "namirasoft-core";
import { WebAuthnPrfOperation } from "namirasoft-site";
import { PasswordName } from "./PasswordName";

// Master-key storage, encrypted at rest with a WebAuthn PRF-derived AES key.
//
//   - credentialId + ciphertext live in localStorage (via IStorageLocal).
//   - derived AES key is cached in sessionStorage (via IStorageSession) with a
//     sliding idle timeout equal to the user's chosen retention duration.
//   - the PRF ceremony (passkey prompt) runs on the first save/load after each
//     browser session, or whenever the idle timeout elapses.
export class Storage
{
    private static instance?: WebAuthnPrfOperation;

    private static operation(): WebAuthnPrfOperation
    {
        if (!Storage.instance)
        {
            const persistent: IStorage = new IStorageLocal();
            const session: IStorage = new IStorageSession();
            Storage.instance = new WebAuthnPrfOperation({
                rpName: "Namirasoft Password",
                persistent,
                session,
                namespace: "ns-password",
            });
        }
        return Storage.instance;
    }

    static async setMasterKey(user_id: string, master_key: string, ttl_ms: number): Promise<void>
    {
        const op = Storage.operation();
        op.setIdleTimeoutMs(ttl_ms);
        if (!op.isRegistered())
            await op.register();
        await op.save(PasswordName.storage.MASTER_KEY(user_id), master_key);
    }

    static async getMasterKey(user_id: string): Promise<string>
    {
        try
        {
            return await Storage.operation().load(PasswordName.storage.MASTER_KEY(user_id));
        }
        catch (error)
        {
            // Blob missing, cache cold, or ciphertext encrypted with a different
            // passkey than the one currently registered. Surface for debugging.
            console.warn("Storage.getMasterKey failed:", error);
            return "";
        }
    }

    static hasMasterKey(user_id: string): boolean
    {
        const op = Storage.operation();
        if (!op.isRegistered() || !op.isUnlocked()) return false;
        return op.list().includes(PasswordName.storage.MASTER_KEY(user_id));
    }

    static isPasskeyRegistered(): boolean
    {
        return Storage.operation().isRegistered();
    }

    static isUnlocked(): boolean
    {
        return Storage.operation().isUnlocked();
    }

    static lock(): void
    {
        Storage.operation().lock();
    }

    static async unlockPasskey(): Promise<void>
    {
        await Storage.operation().unlock();
    }

    static resetPasskey(): void
    {
        Storage.operation().reset();
    }

    static async delMasterKey(user_id: string): Promise<void>
    {
        const op = Storage.operation();
        op.remove(PasswordName.storage.MASTER_KEY(user_id));
        op.lock();
    }

    static getDefaultRetentionDuration(): number | null
    {
        let storage = new IStorageLocal();
        let value = storage.get(PasswordName.storage.RETENTION_DURATION(), "");
        return value ? Number(value) : null;
    }
    static setDefaultRetentionDuration(ttl_ms: number): void
    {
        let storage = new IStorageLocal();
        storage.set(PasswordName.storage.RETENTION_DURATION(), String(ttl_ms));
    }

    static getUser(user_id: string): string
    {
        let storage = new IStorageLocal();
        return storage.get(PasswordName.storage.USER(user_id), "");
    }
    static delUser(user_id: string): void
    {
        let storage = new IStorageLocal();
        storage.del(PasswordName.storage.USER(user_id));
    }
}